import {
  LinearProgress,
  LinearProgressProps,
  SxProps,
  Theme,
  linearProgressClasses,
  styled,
} from "@mui/material";

const StyledVerticalLinearProgress = styled(LinearProgress)(
  ({ theme, value }) => ({
    borderRadius: 5,
    width: "8px",
    height: "100%",
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.progressBar.background,
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: theme.palette.progressBar.primary,
      transform: `translateY(-${value}%) !important`,
    },
  }),
);

export interface VerticalLinearProgressProps extends LinearProgressProps {
  /**
   * 1-based index of the active step.
   * Pass together with `totalSteps` and `stepLabel`.
   */
  currentStep?: number;
  /** Total number of steps in the flow. */
  totalSteps?: number;
  /** Name of the active step, for example "Wallbox". */
  stepLabel?: string;
}

/**
 * Vertical progress for multi-step flows.
 *
 * The bar always has the accessible name "Progress". When the active step is
 * provided, `aria-valuenow` and `aria-valuetext` describe that step
 * (for example, valuetext "Step 1 of 3: Wallbox").
 */
const VerticalLinearProgress = ({
  currentStep,
  totalSteps,
  stepLabel,
  ...props
}: VerticalLinearProgressProps) => {
  const hasActiveStep =
    typeof currentStep === "number" &&
    typeof totalSteps === "number" &&
    totalSteps > 0 &&
    typeof stepLabel === "string" &&
    stepLabel.length > 0;

  return (
    <StyledVerticalLinearProgress
      {...props}
      aria-label="Progress"
      {...(hasActiveStep
        ? {
            "aria-valuenow": currentStep,
            "aria-valuemin": 1,
            "aria-valuemax": totalSteps,
            "aria-valuetext": `Step ${currentStep} of ${totalSteps}: ${stepLabel}`,
          }
        : {})}
    />
  );
};

export const stepProgressProps = (
  labels: readonly string[],
  currentStep: number,
): Pick<
  VerticalLinearProgressProps,
  "currentStep" | "totalSteps" | "stepLabel"
> => {
  const stepLabel = labels[currentStep - 1] ?? "";

  return {
    currentStep,
    totalSteps: labels.length,
    stepLabel,
  };
};

/**
 * Desktop shows the step menu. On small screens the same menu stays in the
 * accessibility tree so the current step is still announced.
 */
export const progressMenuSx: SxProps<Theme> = {
  "@media (max-width:599.95px)": {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    whiteSpace: "nowrap",
    border: 0,
  },
};

export default VerticalLinearProgress;
